import { lazy } from 'react';
import { Navigate, RouteObject, useLocation } from 'react-router-dom';

import Home from '@/pages/home';
import HomeDefaultRedirect from '@/routes/components/home-default-redirect';
import { lazyLoad } from '@/routes/components/lazy-route';
import Profile from '@/pages/profile';
import CompanySettings from '@/pages/company-settings';
import Investors from '@/pages/investors';
import MeetingHub from '@/pages/meeting-hub';
import Members from '@/pages/members';
import FileHub from '@/pages/filehub';
import RiskAssessment from '@/pages/risk-assessment';
import GoogleAccountOauth from '@/pages/meeting-hub/oauth';
// import InvestorFinancialPlan from "@/pages/financial-plan/investor-plan";
// import FinancialPlanEdit from "@/pages/financial-plan/edit-plan/index";
import Dashboard from '@/pages/dashboard';
import AdvisorPrepDashboard from '@/pages/advisor-prep-dashboard';
import Canvas from '@/pages/canvas';
import SubsManagement from '@/pages/subs-management/subs-management';
import ProtectedRoute from '@/common/components/ProtectedRoute';
import PublicRoute from '@/common/components/PublicRoute';
import { SignUp } from '@/pages/login/signup';
import PlanningStudio from '@/pages/planning-studio';
import { StripeSelectProduct } from '@/pages/stripe/select-product';
import CustomizedTemplatePreview from '@/pages/planning-studio/planning-templates/customized-template-preview';
import InvestorProfilePage from '@/pages/investors/profile';
import InvestorPlanPage from '@/pages/financial-plan/investor-plan-page';
import { TransactionHistoryPage } from '@/pages/fees/transaction-history';
import SubscriptionRequiredPage from '@/pages/subscription-required';
import RedirectLegacyInvestorOperationsRoute from '@/routes/components/redirect-legacy-investor-operations-route';
const GuidedModeEntryV2 = lazy(() => import('@/pages/canvas/guided-v2'));
const InvestorDetail = lazy(() => import('@/pages/investors/investor-detail'));
const NewInvestorPage = lazy(() => import('@/pages/investors/new-investor'));
const Login = lazy(() => import('../pages/login'));
const ResetPassword = lazy(() => import('../pages/login/reset-password'));
const CompleteProfile = lazy(() => import('../pages/login/complete-profile'));

function RedirectToGuidedV2() {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/home/planning/guided-mode-v2',
        search: location.search,
        hash: location.hash,
      }}
      replace
      state={location.state ?? null}
    />
  );
}

function RedirectLegacyCustomersRoute() {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: location.pathname.replace('/home/customers', '/home/clients'),
        search: location.search,
        hash: location.hash,
      }}
      replace
      state={location.state ?? null}
    />
  );
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/login',
    element: <PublicRoute redirectAuthenticated={false}>{lazyLoad(<Login />)}</PublicRoute>,
  },
  {
    path: '/firm/:firmId',
    element: <Navigate to="login" replace />,
  },
  {
    path: '/firm/:firmId/login',
    element: <PublicRoute redirectAuthenticated={false}>{lazyLoad(<Login />)}</PublicRoute>,
  },
  {
    path: '/sign-up',
    element: <PublicRoute>{lazyLoad(<SignUp />)}</PublicRoute>,
  },
  {
    path: '/reset-password',
    element: <PublicRoute>{lazyLoad(<ResetPassword />)}</PublicRoute>,
  },
  {
    path: '/firm/:firmId/reset-password',
    element: <PublicRoute>{lazyLoad(<ResetPassword />)}</PublicRoute>,
  },
  {
    path: '/complete-profile',
    element: lazyLoad(<CompleteProfile />),
  },
  {
    path: '/meetinghub/oauth',
    element: lazyLoad(<GoogleAccountOauth />),
  },
  {
    path: '/stripe-config',
    element: lazyLoad(<StripeSelectProduct />),
  },
  {
    path: '/subscriptions',
    element: lazyLoad(<SubscriptionRequiredPage />),
  },
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <Home />
      </ProtectedRoute>
    ),
    children: [
      { path: '', element: <HomeDefaultRedirect /> },
      { path: 'dashboard', element: lazyLoad(<Dashboard />) },
      {
        path: 'advisor-prep-dashboard',
        element: lazyLoad(
          <ProtectedRoute featureFlag="advisor_prep_dashboard_access">
            <AdvisorPrepDashboard />
          </ProtectedRoute>,
        ),
      },
      // {
      //   path: "investor-financial-plan",
      //   element: lazyLoad(<InvestorFinancialPlan />),
      // },
      // { path: "financial-plan-edit", element: lazyLoad(<FinancialPlanEdit />) },
      {
        path: 'customers/*',
        element: <RedirectLegacyCustomersRoute />,
      },
      {
        path: 'clients',
        element: lazyLoad(<Investors />),
        children: [
          {
            path: 'new',
            element: lazyLoad(<NewInvestorPage />),
          },
          {
            path: ':investorId/operations',
            element: <RedirectLegacyInvestorOperationsRoute />,
          },
          {
            path: ':investorId',
            element: lazyLoad(<InvestorDetail />),
          },
          {
            path: ':investorId/profile',
            element: lazyLoad(<InvestorDetail initialTabKey="profile" />),
          },
          {
            path: ':investorId/plan/:planId',
            element: lazyLoad(<InvestorPlanPage />),
          },
        ],
      },
      { path: 'meetinghub', element: lazyLoad(<MeetingHub />) },
      { path: 'risk-assessment', element: lazyLoad(<RiskAssessment />) },
      { path: 'filehub', element: lazyLoad(<FileHub />) },
      { path: 'members', element: lazyLoad(<Members />) },
      { path: 'profile', element: lazyLoad(<Profile />) },
      { path: 'company-settings', element: lazyLoad(<CompanySettings />) },
      { path: 'subscription', element: lazyLoad(<SubsManagement />) },
      {
        path: 'planning',
        element: lazyLoad(
          <ProtectedRoute featureFlag="canvas_access">
            <Canvas />
          </ProtectedRoute>,
        ),
        children: [
          {
            path: 'sandbox-mode',
            element: <RedirectToGuidedV2 />,
          },
          {
            path: 'guided-mode',
            element: <RedirectToGuidedV2 />,
          },
          {
            path: 'guided-mode-v2',
            element: lazyLoad(<GuidedModeEntryV2 />),
          },
        ],
      },
      {
        path: 'planning-templates',
        element: lazyLoad(
          <ProtectedRoute featureFlag="canvas_access">
            <PlanningStudio />
          </ProtectedRoute>,
        ),
        children: [
          {
            path: 'customized-template-preview',
            element: lazyLoad(<CustomizedTemplatePreview />),
          },
        ],
      },
      {
        path: 'transaction-history',
        element: lazyLoad(<TransactionHistoryPage />),
      },
    ],
  },
];

export default routes;
